import { z } from 'zod'

/**
 * A tela que o desktop baixa do servidor (F213, ADR-0040).
 *
 * O shell guarda a última tela boa e diz qual a página abriu (`page`) e qual
 * um recarregar abriria (`latest`). Fora do desktop o endpoint não existe, e
 * tudo aqui devolve `null` calado.
 */

export const DesktopUiStatus = z.object({
  page: z.string().nullable(),
  latest: z.string().nullable(),
  shell: z.string(),
})

export type DesktopUiStatus = z.infer<typeof DesktopUiStatus>

const BASE = '/__aeris/ui'

export const readDesktopUi = async (
  signal?: AbortSignal
): Promise<DesktopUiStatus | null> => {
  try {
    const response = await fetch(BASE, {
      ...(signal === undefined ? {} : { signal }),
      headers: { accept: 'application/json' },
    })
    if (!response.ok) return null
    const parsed = DesktopUiStatus.safeParse(await response.json())
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

const post = async (
  action: 'booted' | 'check' | 'reset'
): Promise<DesktopUiStatus | null> => {
  try {
    const response = await fetch(`${BASE}/${action}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: '{}',
    })
    if (!response.ok) return null
    const parsed = DesktopUiStatus.safeParse(await response.json())
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

/** A página abriu: a versão dela é boa, e o shell não volta atrás. */
export const confirmDesktopUi = async (): Promise<DesktopUiStatus | null> =>
  await post('booted')

/** Procurar tela nova agora, em vez de esperar os 10 minutos. */
export const checkDesktopUi = async (): Promise<DesktopUiStatus | null> =>
  await post('check')

/** Voltar para a tela que veio no instalador. */
export const resetDesktopUi = async (): Promise<DesktopUiStatus | null> =>
  await post('reset')

/** Há uma tela mais nova pronta, e esta página ainda é a antiga? */
export const updateReady = (status: DesktopUiStatus | null): boolean =>
  status !== null && status.latest !== null && status.latest !== status.page
